/**
 * ehr#727 (PEER_SUPPORT_NOTE_10-30-V8, Jay's 11a, 12a and 13a): the Peer support note's own parts
 * on the live note: Recovery goal, Peer support activities, and the peer specialist credential
 * under the signature. The editor (ClinicalNotesPanel) puts them in its section cards.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  PEER_ACTIVITIES,
  PEER_ACTIVITY_MAX,
  PEER_CREDENTIAL_LABEL,
  PEER_CREDENTIAL_MAX,
  PEER_CREDENTIALS,
  type PeerLists,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { activityPills, credentialChoices, togglePicked } from '../lib/peerNote.js';
import { Dd } from './Dropdown.js';
import { DictateMic, type DictateOpts } from './DictateMic.js';

/**
 * The practice's lists (GET /practice/peer-lists) and the signed-in person's own credential (GET
 * /me/peer-credential), read when the note is a Peer support note. `mine` is null until read.
 */
export function usePeerSupport(on: boolean) {
  const [lists, setLists] = useState<PeerLists>({
    activities: [...PEER_ACTIVITIES],
    credentials: [...PEER_CREDENTIALS],
  });
  const [mine, setMine] = useState<string | null>(null);
  useEffect(() => {
    if (!on) return;
    let live = true;
    api
      .get<PeerLists>('/practice/peer-lists')
      .then((l) => live && setLists(l))
      .catch(() => undefined);
    api
      .get<{ credential: string }>('/me/peer-credential')
      .then((r) => live && setMine(r.credential))
      .catch(() => live && setMine(''));
    return () => {
      live = false;
    };
  }, [on]);
  type Added = PeerLists & { name: string; added: boolean };
  /** + Add: a new activity on the practice list; the name as the list spells it. */
  const addActivity = (name: string) =>
    api.post<Added>('/practice/peer-lists', { list: 'activities', name }).then((r) => {
      setLists({ activities: r.activities, credentials: r.credentials });
      return r.name;
    });
  /** Save in the credential editor: a new one goes on the practice list first. */
  const saveCredential = async (credential: string, isNew: boolean) => {
    let c = credential;
    if (isNew && c) {
      const r = await api.post<Added>('/practice/peer-lists', { list: 'credentials', name: c });
      setLists({ activities: r.activities, credentials: r.credentials });
      c = r.name || c;
    }
    const r = await api.put<{ credential: string }>('/me/peer-credential', { credential: c });
    setMine(r.credential);
    return r.credential;
  };
  return { lists, mine, addActivity, saveCredential };
}

/** V8 12a: one line in the client's own words, with its mic and the "From MM/DD/YYYY" tag. */
export function RecoveryGoalField(p: {
  value: string;
  onChange: (v: string) => void;
  /** "From 10/13/2026" while carried forward unedited, else ''. */
  fromTag: string;
  dictate: DictateOpts;
  disabled: boolean;
  label: string;
  placeholder: string;
}) {
  // One line that grows as it wraps, and again when its width changes (V8: never an inner scroll)
  const ref = useRef<HTMLTextAreaElement>(null);
  const grow = () => {
    const t = ref.current;
    if (!t) return;
    t.style.height = 'auto';
    t.style.height = `${t.scrollHeight + t.offsetHeight - t.clientHeight}px`;
  };
  useLayoutEffect(grow, [p.value]);
  useEffect(() => {
    const t = ref.current;
    if (!t || typeof ResizeObserver === 'undefined') return;
    let w = t.clientWidth;
    const ro = new ResizeObserver(() => {
      if (t.clientWidth === w) return;
      w = t.clientWidth;
      grow();
    });
    ro.observe(t);
    return () => ro.disconnect();
  }, []);
  return (
    <>
      <div className="pn-ta-wrap">
        <textarea
          ref={ref}
          id="recGoal"
          className="pn-extra-ta pn-peer-goal"
          rows={1}
          value={p.value}
          placeholder={p.placeholder}
          aria-label={p.label}
          readOnly={p.disabled}
          onChange={(e) => p.onChange(e.target.value)}
        />
        {!p.disabled && (
          <DictateMic dictate={p.dictate} label={p.label} value={p.value} onText={p.onChange} />
        )}
      </div>
      {p.fromTag && (
        <span className="pn-tag pn-peer-from" data-test="rec-from">
          {p.fromTag}
        </span>
      )}
    </>
  );
}

/** V8 11a: the activity pills (more than one), with "Search or add an activity" and + Add. */
export function PeerActivitiesField(p: {
  items: readonly string[];
  picked: readonly string[];
  onChange: (picked: string[]) => void;
  /** Saves a new activity on the practice list; resolves to its name as the list spells it. */
  onAdd: (name: string) => Promise<string>;
  onNotice: (msg: string) => void;
  disabled: boolean;
  label: string;
}) {
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const pills = activityPills(p.items, p.picked, q);
  const pick = (name: string) => {
    if (!p.picked.some((x) => x.toLowerCase() === name.toLowerCase()))
      p.onChange([...p.picked, name]);
  };
  const add = () => {
    const t = q.replace(/\s+/g, ' ').trim();
    if (!t) {
      setErr('Type the activity first.');
      return;
    }
    const known = p.items.find((x) => x.toLowerCase() === t.toLowerCase());
    if (known) {
      pick(known);
      setQ('');
      p.onNotice(`${known} picked`);
      return;
    }
    setBusy(true);
    p.onAdd(t)
      .then((name) => {
        pick(name);
        setQ('');
        p.onNotice(`${name} added to the practice list`);
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not add the activity. Try again.'),
      )
      .finally(() => setBusy(false));
  };
  return (
    <div className="pn-peer-acts">
      <div className="pn-chip-grid" role="group" aria-label={p.label}>
        {pills.map((a) => (
          <button
            key={a.name}
            type="button"
            className={`pn-chip${a.on ? ' active' : ''}`}
            aria-pressed={a.on}
            data-cl="activities"
            disabled={p.disabled}
            onClick={() => p.onChange(togglePicked(p.picked, a.name))}
          >
            {a.name}
          </button>
        ))}
      </div>
      {!p.disabled && (
        <>
          <div className="pn-peer-add">
            <input
              className="pn-peer-q"
              value={q}
              maxLength={PEER_ACTIVITY_MAX}
              placeholder="Search or add an activity"
              aria-label="Search or add an activity"
              autoComplete="off"
              onChange={(e) => {
                setQ(e.target.value);
                setErr('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <button type="button" className="btn small primary" disabled={busy} onClick={add}>
              + Add
            </button>
          </div>
          {err && (
            <em className="pn-peer-err" role="alert">
              {err}
            </em>
          )}
        </>
      )}
    </div>
  );
}

/**
 * V8 13a: the credential under the signature. The signer's own (Change, a picker with
 * + Add credential, Cancel next to Save); None on file in red. Read-only on a signed note or
 * someone else's.
 */
export function PeerCredentialRow(p: {
  credential: string;
  choices: readonly string[];
  editable: boolean;
  onSave: (credential: string, isNew: boolean) => Promise<string>;
  onNotice: (msg: string) => void;
}) {
  const [edit, setEdit] = useState(false);
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState('');
  const [fresh, setFresh] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const changeRef = useRef<HTMLButtonElement>(null);
  const backToChange = useRef(false);
  useEffect(() => {
    if (!edit && backToChange.current) {
      backToChange.current = false;
      changeRef.current?.focus();
    }
  }, [edit]);
  const open = () => {
    setVal(p.credential);
    setFresh('');
    setAdding(false);
    setErr('');
    setEdit(true);
  };
  const cancel = () => {
    backToChange.current = true;
    setEdit(false);
  };
  const save = () => {
    const typed = fresh.replace(/\s+/g, ' ').trim();
    const next = adding && typed ? typed : val;
    setBusy(true);
    p.onSave(next, adding && !!typed)
      .then((c) => {
        backToChange.current = true;
        setEdit(false);
        p.onNotice(c ? `Credential set to ${c}` : 'Credential removed');
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not save the credential. Try again.'),
      )
      .finally(() => setBusy(false));
  };
  const keys = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      cancel();
    }
  };
  return (
    <div
      className="pn-sig-row pn-peer-cred"
      data-test="peer-cred"
      data-pn-target="peer-credential"
      onKeyDown={edit ? keys : undefined}
    >
      <div className="pn-peer-cred-main">
        <div className="pn-sig-role">{PEER_CREDENTIAL_LABEL}</div>
        {!edit ? (
          <div className="pn-sig-name">
            {p.credential ? p.credential : <span className="pn-peer-none">None on file</span>}
          </div>
        ) : (
          <div className="pn-peer-cred-edit">
            <span className="pn-peer-cred-pick">
              <Dd
                value={val}
                onChange={setVal}
                searchable={false}
                ariaLabel={PEER_CREDENTIAL_LABEL}
                options={[
                  { value: '', label: 'None on file' },
                  ...credentialChoices(p.choices, p.credential).map((c) => ({
                    value: c,
                    label: c,
                  })),
                ]}
              />
            </span>
            {adding ? (
              <input
                className="pn-peer-q"
                value={fresh}
                autoFocus
                maxLength={PEER_CREDENTIAL_MAX}
                placeholder="New credential"
                aria-label="New credential"
                onChange={(e) => setFresh(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    save();
                  }
                }}
              />
            ) : (
              <button type="button" className="pn-link" onClick={() => setAdding(true)}>
                + Add credential
              </button>
            )}
            <span className="pn-peer-cred-btns">
              <button type="button" className="btn small" onClick={cancel}>
                Cancel
              </button>
              <button type="button" className="btn small primary" disabled={busy} onClick={save}>
                Save
              </button>
            </span>
            {err && (
              <em className="pn-peer-err" role="alert">
                {err}
              </em>
            )}
          </div>
        )}
      </div>
      {p.editable && !edit && (
        <button ref={changeRef} type="button" className="pn-link" onClick={open}>
          Change
        </button>
      )}
    </div>
  );
}
